import { Button, ButtonGroup, FormGroup, H4, ITreeNode, Menu, MenuItem, Popover, Tree } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IFieldProps } from "decorator-form";
import React from "react";

import { KeyValueListField } from "common/form/fields/KeyValueListField";
import { registerField } from "common/form/register";
import TextWidget from "common/form/widgets/TextWidget";
import { arrayMove } from "core/util/ArrayUtils";
import StringUtil from "core/util/StringUtil";
import { ILinkType, LinkTypeRegistry } from "./ILinkType";
import INavMenuConfig from "./NavMenuConfig";

interface INavMenuFieldState {
	editing: null | string;
	expanded: { [id: string]: boolean; };
}

type NavMenuFieldProps = IFieldProps<INavMenuConfig> & { linkTypes: Array<ILinkType<any>>; };

const toolbarButtonProps = {
	small: true,
	minimal: true
};

export class NavMenuField extends React.Component<NavMenuFieldProps, INavMenuFieldState> {

	public static registerWithTypes(linkTypes: Array<ILinkType<any>>): string {
		return registerField(this.withLinkTypes(linkTypes), "object");
	}

	public static withLinkTypes(linkTypes: Array<ILinkType<any>>) {
		return (props: IFieldProps<INavMenuConfig>) => <NavMenuField {...props} linkTypes={linkTypes} />;
	}

	public state = { editing: null, expanded: {} };

	public render() {
		const { formData, linkTypes } = this.props;

		return (
			<div>
				<H4>{this.props.label || this.props.uiSchema.title}</H4>
				<FormGroup label="Menu">
					<Tree
						contents={formData && formData.ids && formData.ids.map(this.mapIdToNode) || []}
						onNodeCollapse={this.setExpanded(false)}
						onNodeExpand={this.setExpanded(true)}
						onNodeClick={this.toggleEditing}
					/>
				</FormGroup>
				<FormGroup>
					<Popover content={
						<Menu>
							{
								linkTypes.map((link) => (
									<MenuItem
										key={link.type}
										text={link.label}
										onClick={this.addChild(link.type)}
									/>
								))
							}
						</Menu>
					}>
						<Button small={true} icon={IconNames.SMALL_PLUS} text="Add Link"  />
					</Popover>
				</FormGroup>
				{this.renderSelectedForm()}
			</div>
		);
	}

	// TODO make this use generated forms
	private renderSelectedForm = () => {
		const { formData } = this.props;

		if (!formData || !this.state.editing || !formData.data || !formData.data[this.state.editing]) {
			return null;
		}

		const linkData = formData.data[this.state.editing];

		const linkType = LinkTypeRegistry.getType(linkData.type);

		return (
			<React.Fragment key={this.state.editing}>
				<FormGroup label="Edit Name">
					<TextWidget onChange={this.onChangeEditName(this.state.editing)} value={linkData.editName} />
				</FormGroup>
				{
					typeof linkType.renderInput === 'function' && linkType.renderInput(linkData.data, this.onChangeLinkData(this.state.editing))
				}
			</React.Fragment>
		);
	}

	private mapIdToNode = (id: string, index: number, arr: string[], parentId?: string): ITreeNode<string> => {

		const { formData: { data = {} } = {}, linkTypes } = this.props;

		const link = data[id];

		if (!link) {
			return {
				id: "invalid" + index,
				label: "INVALID LINK ID!",
				secondaryLabel: (
					<ButtonGroup onClick={(e) => e.stopPropagation()}>
						<Button
							{...toolbarButtonProps}
							icon={IconNames.SMALL_CROSS}
							onClick={this.removeLink(id)}
						/>
					</ButtonGroup>
				)
			};
		}

		const linkType = LinkTypeRegistry.getType(link.type);

		if (!linkType) {
			return {
				id: "invalid" + index,
				label: "INVALID LINK TYPE!",
				secondaryLabel: (
					<ButtonGroup onClick={(e) => e.stopPropagation()}>
						<Button
							{...toolbarButtonProps}
							icon={IconNames.SMALL_CROSS}
							onClick={this.removeLink(id)}
						/>
					</ButtonGroup>
				)
			};
		}

		return {
			id,
			nodeData: id,
			label: link.editName || linkType.label,
			icon: linkType.icon,
			childNodes: link.children && link.children.map((cItem, cIndex, cArr) => this.mapIdToNode(cItem, cIndex, cArr, id)),
			isExpanded: this.state.expanded[id],
			isSelected: this.state.editing === id,
			secondaryLabel: (
				<ButtonGroup onClick={(e) => e.stopPropagation()}>
					{
						linkType.canAddChildren !== false && (
							<Popover content={
								<Menu>
									{
										linkTypes.map((lt) => (
											<MenuItem
												key={lt.type}
												text={lt.label}
												onClick={this.addChild(lt.type, id)}
											/>
										))
									}
								</Menu>
							}>
								<Button {...toolbarButtonProps} icon={IconNames.SMALL_PLUS} />
							</Popover>
						)
					}
					<Button
						{...toolbarButtonProps}
						icon={IconNames.CHEVRON_UP}
						disabled={index < 1}
						onClick={this.onSort(index, index - 1, parentId)}
					/>
					<Button
						{...toolbarButtonProps}
						icon={IconNames.CHEVRON_DOWN}
						disabled={index >= arr.length - 1}
						onClick={this.onSort(index, index + 1, parentId)}
					/>
					<Button
						{...toolbarButtonProps}
						icon={IconNames.SMALL_CROSS}
						onClick={this.removeLink(id)}
					/>
				</ButtonGroup>
			)
		};
	}

	private setExpanded = (setTo: boolean) => (node: ITreeNode<string>) => this.setState({expanded: {...this.state.expanded, [node.nodeData]: setTo}});
	private toggleEditing = (node: ITreeNode<string>) => this.setState({editing: this.state.editing === node.nodeData ? null : node.nodeData});

	private onChangeLinkData = (id: string) => (data: any) => {
		const { formData = {} as any, onChange } = this.props;

		onChange({
			...formData,
			data: {
				...formData.data || {},
				[id]: {
					...formData.data[id] || {},
					data
				}
			}
		});
	}

	private removeLink = (id: string) => () => {
		const { formData = {} as any, onChange } = this.props;

		onChange({
			ids: formData.ids && formData.ids.filter((i: string) => i !== id) || [],
			data: Object.entries(formData.data || {}).reduce((a, [linkId, linkConfig]: any) => {
				if (linkId !== id) {
					a[linkId] = {...linkConfig, children: linkConfig.children ? linkConfig.children.filter((cid: string) => cid !== id) : undefined };
				}
				return a;
			}, {})
		});
	}

	private onSort = (currentIndex: number, targetIndex: number, parentId?: string) => () => {
		const { formData, onChange } = this.props;
		if (parentId) {
			onChange({
				...formData,
				data: {
					...formData.data,
					[parentId]: {
						...formData.data[parentId],
						children: arrayMove([...formData.data[parentId].children], currentIndex, targetIndex)
					}
				}
			});
		} else {
			onChange({
				...formData,
				ids: arrayMove([...formData.ids], currentIndex, targetIndex)
			});
		}
	}

	private onChangeEditName = (id: string) => (editName: string) => {
		const { formData, onChange } = this.props;

		onChange({
			...formData,
			data: {
				...formData.data,
				[id]: {
					...formData.data[id],
					editName
				}
			}
		});
	}

	private addChild = (type: string, to?: string) => () => {
		const { formData, onChange } = this.props;

		const id = StringUtil.generateUuid();

		if (formData) {
			if (to) {
				onChange({
					...formData,
					data: {
						...formData.data || {},
						[to]: {
							...formData.data[to],
							children: (
								formData.data[to].children ? [...formData.data[to].children, id] : [id]
							)
						},
						[id]: {
							type,
							data: { }
						}
					}
				});
			} else {
				onChange({
					data: {
						...formData.data,
						[id]: { type, data: {} }
					},
					ids: [...formData.ids || [], id]
				});
			}
		} else {
			onChange({ids: [id], data: { [id]: { type, data: {} }}});
		}
	}
}



// WEBPACK FOOTER //
// ./src/common/views/menuTree/NavMenuField.tsx